// Checkout Page
// Desktop version (≥768px)

import { useState, useEffect, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart } from '@/contexts/CartContext';
import DesktopLayout from '@/components/layout/DesktopLayout';
import { orderApi } from '@/services/orderApi';
import type { CartItem as CartItemType } from '@/types';
import styles from './CheckoutPage.module.css';

interface FormData {
  recipientName: string;
  recipientPhone: string;
  recipientAddress: string;
  notes: string;
}

interface FormErrors {
  recipientName?: string;
  recipientPhone?: string;
  recipientAddress?: string;
}

export default function CheckoutPage() {
  const { items, total, isLoading, refreshCart } = useCart();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Form state
  const [formData, setFormData] = useState<FormData>({
    recipientName: '',
    recipientPhone: '',
    recipientAddress: '',
    notes: '',
  });

  const [formErrors, setFormErrors] = useState<FormErrors>({});

  // Redirect if cart is empty
  useEffect(() => {
    if (!isLoading && items.length === 0) {
      navigate('/cart');
    }
  }, [items, isLoading, navigate]);

  // Sync cart on page load
  useEffect(() => {
    refreshCart();
  }, [refreshCart]);

  // Format price
  const formatPrice = (price: number) => {
    return `$${Math.round(price).toLocaleString()}`;
  };

  // Validate form
  const validateForm = (): boolean => {
    const errors: FormErrors = {};

    if (!formData.recipientName.trim()) {
      errors.recipientName = '請輸入收件人姓名';
    }

    if (!formData.recipientPhone.trim()) {
      errors.recipientPhone = '請輸入聯絡電話';
    } else if (!/^09\d{8}$/.test(formData.recipientPhone.trim())) {
      errors.recipientPhone = '請輸入有效的手機號碼格式 (09xxxxxxxx)';
    }

    if (!formData.recipientAddress.trim()) {
      errors.recipientAddress = '請輸入配送地址';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error for this field
    if (name in formErrors) {
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    }
  };

  // Handle form submit
  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      const response = await orderApi.createOrder({
        recipientName: formData.recipientName.trim(),
        recipientPhone: formData.recipientPhone.trim(),
        recipientAddress: formData.recipientAddress.trim(),
        notes: formData.notes.trim() || undefined,
      });

      // Navigate to order confirmation page
      navigate('/checkout/success', { state: { order: response.data } });
    } catch (err) {
      setError(err instanceof Error ? err.message : '建立訂單失敗，請稍後再試');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <DesktopLayout>
        <div className={styles.checkoutPage}>
          <div className={styles.container}>
            <div className={styles.loading}>載入中...</div>
          </div>
        </div>
      </DesktopLayout>
    );
  }

  return (
    <DesktopLayout>
      <div className={styles.checkoutPage}>
      <div className={styles.container}>
        <h1 className={styles.title}>結帳</h1>

        {error && (
          <div className={styles.error}>
            {error}
            <button onClick={() => setError(null)} className={styles.closeButton}>×</button>
          </div>
        )}

        <div className={styles.checkoutContent}>
          {/* Cart Items Summary */}
          <div className={styles.itemsSection}>
            <h2 className={styles.sectionTitle}>訂單商品</h2>
            <div className={styles.itemsList}>
              {items.map((item: CartItemType) => (
                <div key={item.id} className={styles.item}>
                  <div className={styles.itemInfo}>
                    <h3 className={styles.itemTitle}>{item.product.title}</h3>
                    {item.variantAttributes && (
                      <p className={styles.itemVariant}>
                        {Object.entries(item.variantAttributes)
                          .map(([k, v]) => `${k}: ${v}`)
                          .join(', ')}
                      </p>
                    )}
                    <p className={styles.itemQuantity}>數量: {item.quantity}</p>
                  </div>
                  <p className={styles.itemSubtotal}>{formatPrice(item.subtotal)}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Checkout Form */}
          <div className={styles.formSection}>
            <h2 className={styles.sectionTitle}>收件人資訊</h2>
            <form onSubmit={handleSubmit} className={styles.form}>
              <div className={styles.formGroup}>
                <label htmlFor="recipientName" className={styles.label}>
                  收件人姓名 <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="recipientName"
                  name="recipientName"
                  value={formData.recipientName}
                  onChange={handleInputChange}
                  className={`${styles.input} ${formErrors.recipientName ? styles.inputError : ''}`}
                  disabled={isSubmitting}
                />
                {formErrors.recipientName && (
                  <span className={styles.errorText}>{formErrors.recipientName}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="recipientPhone" className={styles.label}>
                  聯絡電話 <span className={styles.required}>*</span>
                </label>
                <input
                  type="tel"
                  id="recipientPhone"
                  name="recipientPhone"
                  value={formData.recipientPhone}
                  onChange={handleInputChange}
                  placeholder="09xxxxxxxx"
                  className={`${styles.input} ${formErrors.recipientPhone ? styles.inputError : ''}`}
                  disabled={isSubmitting}
                />
                {formErrors.recipientPhone && (
                  <span className={styles.errorText}>{formErrors.recipientPhone}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="recipientAddress" className={styles.label}>
                  配送地址 <span className={styles.required}>*</span>
                </label>
                <input
                  type="text"
                  id="recipientAddress"
                  name="recipientAddress"
                  value={formData.recipientAddress}
                  onChange={handleInputChange}
                  placeholder="請輸入完整配送地址"
                  className={`${styles.input} ${formErrors.recipientAddress ? styles.inputError : ''}`}
                  disabled={isSubmitting}
                />
                {formErrors.recipientAddress && (
                  <span className={styles.errorText}>{formErrors.recipientAddress}</span>
                )}
              </div>

              <div className={styles.formGroup}>
                <label htmlFor="notes" className={styles.label}>
                  備註
                </label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="如有特殊需求請在此說明"
                  rows={3}
                  className={styles.textarea}
                  disabled={isSubmitting}
                />
              </div>

              {/* Order Summary */}
              <div className={styles.orderSummary}>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>商品小計</span>
                  <span className={styles.summaryValue}>{formatPrice(total)}</span>
                </div>
                <div className={styles.summaryRow}>
                  <span className={styles.summaryLabel}>運費</span>
                  <span className={styles.summaryValue}>免運</span>
                </div>
                <div className={`${styles.summaryRow} ${styles.totalRow}`}>
                  <span className={styles.summaryLabel}>總計</span>
                  <span className={`${styles.summaryValue} ${styles.totalValue}`}>
                    {formatPrice(total)}
                  </span>
                </div>
              </div>

              <button
                type="submit"
                className={styles.submitButton}
                disabled={isSubmitting}
              >
                {isSubmitting ? '建立訂單中...' : `確認訂單 ${formatPrice(total)}`}
              </button>
            </form>
          </div>
        </div>
      </div>
      </div>
    </DesktopLayout>
  );
}
